import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * StridesBalance — small pill widget that shows the user's current Strides balance.
 * Polls /api/strides/balance every 30s while mounted. Listens for the
 * `strides:updated` window event for instant updates after earn actions.
 */
const StridesBalance = ({ compact = false, onClick }) => {
  const { token, user } = useAuth();
  const [balance, setBalance] = useState(null);

  const fetchBalance = useCallback(async () => {
    if (!token || !user) return;
    try {
      const { data } = await axios.get(`${API}/strides/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBalance(data?.balance ?? 0);
    } catch (_) {
      // silent — balance is best-effort UI sugar
    }
  }, [token, user]);

  useEffect(() => {
    if (!token || !user) {
      setBalance(null);
      return undefined;
    }
    fetchBalance();
    const interval = setInterval(fetchBalance, 30000);
    const handler = () => fetchBalance();
    window.addEventListener('strides:updated', handler);
    return () => {
      clearInterval(interval);
      window.removeEventListener('strides:updated', handler);
    };
  }, [token, user, fetchBalance]);

  if (!token || balance === null) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="strides-balance-pill"
      className={`inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-black ${compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'} shadow-md hover:shadow-lg active:scale-95 transition-all`}
      title="Your Strides balance"
    >
      <Sparkles className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      <span>{balance.toLocaleString()}</span>
      {!compact && <span className="font-bold opacity-80">Strides</span>}
    </button>
  );
};

export default StridesBalance;
